import axios from 'axios';
import monitoring from './monitoring';
import { keycloak } from './keycloak';

// Mutable tenant ID — updated when user switches org
export let currentTenantId: string | null = localStorage.getItem('tenantId') ?? import.meta.env.VITE_ORG_ID ?? null;
export const setCurrentTenantId = (id: string | null) => {
  currentTenantId = id;
  if (id) localStorage.setItem('tenantId', id);
  else localStorage.removeItem('tenantId');
};

// IAM service — no X-Tenant-Id needed
export const iamApi = axios.create({
  baseURL: import.meta.env.VITE_IAM_URL || 'https://iam.sudosys.org',
  timeout: 10000,
});
iamApi.interceptors.request.use(async (config) => {
  if (keycloak.authenticated) {
    try { await keycloak.updateToken(30); } catch { /* ignore */ }
    if (keycloak.token) config.headers.Authorization = `Bearer ${keycloak.token}`;
  }
  return config;
});

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:7006/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    // Authorization: keycloak.authenticated ? `Bearer ${getRPT()}` : '',
  },
});

// Request interceptor
api.interceptors.request.use(
  async config => {
    // Add Keycloak auth token if available
    if (keycloak.authenticated) {
      try {
        await keycloak.updateToken(30);
      } catch { /* ignore */ }
      if (keycloak.token) {
        config.headers.Authorization = `Bearer ${keycloak.token}`;
      }
    }
    if (currentTenantId) config.headers['X-Tenant-Id'] = currentTenantId;

    // Add breadcrumb for API request
    monitoring.addBreadcrumb(
      `API Request: ${config.method?.toUpperCase()} ${config.url}`,
      'http',
      {
        method: config.method,
        url: config.url,
        baseURL: config.baseURL,
        authenticated: keycloak.authenticated,
      }
    );

    return config;
  },
  error => {
    // Track request error
    monitoring.captureError({
      message: `API Request Error: ${error.message}`,
      severity: 'medium',
      tags: { type: 'api_request_error' },
      extra: { error },
    });
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  response => {
    // Add breadcrumb for successful response
    monitoring.addBreadcrumb(
      `API Response: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`,
      'http',
      {
        status: response.status,
        method: response.config.method,
        url: response.config.url,
        responseTime:
          Date.now() -
          (response.config as unknown as { startTime: number }).startTime,
      }
    );

    return response;
  },
  error => {
    // Handle common errors
    if (error.response?.status === 401) {
      // Handle unauthorized access - let Keycloak handle the authentication
      // Clear any stored tokens and let the app handle redirect through ProtectedRoute
      localStorage.removeItem('authToken');

      // Track authentication error
      monitoring.captureError({
        message: 'Unauthorized access - authentication required',
        severity: 'medium',
        tags: { type: 'auth_error' },
        extra: {
          status: 401,
          url: error.config?.url,
        },
      });

      // Don't redirect here - let the ProtectedRoute component handle it
      // This ensures proper Keycloak authentication flow
    } else {
      // Track other API errors
      monitoring.captureError({
        message: `API Error: ${error.response?.status || 'Network Error'} - ${error.message}`,
        severity: error.response?.status >= 500 ? 'high' : 'medium',
        tags: {
          type: 'api_error',
          status: error.response?.status?.toString() || 'network',
        },
        extra: {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          statusText: error.response?.statusText,
          responseData: error.response?.data,
        },
      });
    }

    return Promise.reject(error);
  }
);

// API functions
export const fetchExample = async () => {
  const response = await api.get('/example');
  return response.data;
};

export const createExample = async (data: Record<string, unknown>) => {
  const response = await api.post('/example', data);
  return response.data;
};

export const updateExample = async (
  id: string,
  data: Record<string, unknown>
) => {
  const response = await api.put(`/example/${id}`, data);
  return response.data;
};

export const deleteExample = async (id: string) => {
  const response = await api.delete(`/example/${id}`);
  return response.data;
};

export default api;
